import { useCallback, useEffect, useState } from 'react'
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { useSnackbar } from 'notistack'
import { wdBulkUpload, wdBulkUploads, serverMessage } from './api'
import { fmtDate, BULK_ACTIONS, BULK_DEFAULT_REMARK, bulkSampleCsv, bulkFileSummary } from './format'

const muted = { fontSize: 11.5, color: '#6b7280' }
const headSx = { fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#888', fontWeight: 700, py: 0.75 }
const cellSx = { fontSize: 12, py: 0.75, verticalAlign: 'top' }

const saveText = (name, text) => {
  const url = URL.createObjectURL(new Blob(['﻿' + text], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/**
 * Bulk action on many disputes in one go (business, 27 Oct). The team picks the outcome per AWB in the file
 * (the ACTION column); a row with no Remark takes the remark typed here, "Aligned and closed with Merchant"
 * by default. Settle-at-revised-weight rows carry their weight in Revised_Weight_kg. The file goes to the
 * existing bulk endpoint, which runs every row through the same decision code as the drawer, in the
 * background; recent uploads and their per-AWB results are listed below.
 */
export default function BulkActionDialog({ open, onClose }) {
  const { enqueueSnackbar } = useSnackbar()
  const [file, setFile] = useState(null)
  const [remark, setRemark] = useState(BULK_DEFAULT_REMARK)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [uploads, setUploads] = useState([])
  const [loadingUploads, setLoadingUploads] = useState(false)
  const [processedSomething, setProcessedSomething] = useState(false)

  const loadUploads = useCallback(() => {
    setLoadingUploads(true)
    wdBulkUploads()
      .then((files) => setUploads(files))
      .catch((err) => enqueueSnackbar(serverMessage(err, 'Could not load recent uploads'), { variant: 'error' }))
      .finally(() => setLoadingUploads(false))
  }, [enqueueSnackbar])

  useEffect(() => {
    if (!open) return
    setFile(null); setError(''); setRemark(BULK_DEFAULT_REMARK); setProcessedSomething(false)
    loadUploads()
  }, [open, loadUploads])

  // Rows are processed in the background: while an upload is still running, look again every few seconds.
  const running = uploads.some((u) => bulkFileSummary(u).inProgress)
  useEffect(() => {
    if (!open || !running) return undefined
    const t = setInterval(loadUploads, 5000)
    return () => clearInterval(t)
  }, [open, running, loadUploads])

  const onUpload = async () => {
    if (!file) { setError('Choose the CSV file first.'); return }
    if (!remark.trim()) { setError('A remark is needed: it is used for every row whose Remark column is empty.'); return }
    setUploading(true)
    setError('')
    try {
      await wdBulkUpload(file, remark.trim())
      enqueueSnackbar('Uploaded. The rows are being processed; results appear below.', { variant: 'success' })
      setFile(null)
      setProcessedSomething(true)
      loadUploads()
    } catch (err) {
      setError(serverMessage(err, 'Upload failed'))
    } finally {
      setUploading(false)
    }
  }

  const close = () => onClose(processedSomething)

  return (
    <Dialog open={open} onClose={close} maxWidth='md' fullWidth>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pr: 6 }}>
        Bulk action on disputes
        <IconButton aria-label='Close' onClick={close} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon fontSize='small' /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontSize: 12.5, mb: 1 }}>
          Upload a CSV with the columns <b>AWB_Number</b>, <b>ACTION</b>, <b>Remark</b> and <b>Revised_Weight_kg</b>. Each AWB gets the
          outcome in its ACTION column, exactly as if it were decided one by one from the drawer.
        </Typography>
        <Table size='small' sx={{ mb: 1.5 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>ACTION</TableCell>
              <TableCell sx={headSx}>What it does</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {BULK_ACTIONS.map((a) => (
              <TableRow key={a.value}>
                <TableCell sx={{ ...cellSx, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{a.value}</TableCell>
                <TableCell sx={cellSx}>{a.help}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography sx={{ ...muted, mb: 1.5 }}>
          Remark: leave a row's cell empty to use the remark below. Revised_Weight_kg: only for SETTLED_PROSHIP, in kg (e.g. 1.5);
          leave it empty for every other action. Up to 10,000 AWBs per file; a file already uploaded once is refused.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
          <Button size='small' variant='outlined' startIcon={<FileDownloadOutlinedIcon />} onClick={() => saveText('weight-dispute-bulk-action-sample.csv', bulkSampleCsv())}
            sx={{ textTransform: 'none', fontSize: 12 }}>
            Sample file
          </Button>
          <Button size='small' variant='outlined' component='label' startIcon={<UploadFileOutlinedIcon />} sx={{ textTransform: 'none', fontSize: 12 }}>
            {file ? file.name : 'Choose CSV'}
            <input hidden type='file' accept='.csv,text/csv' onChange={(e) => { setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null); setError(''); e.target.value = '' }} />
          </Button>
        </Stack>
        <TextField
          fullWidth size='small' label='Remark for rows without one' value={remark}
          onChange={(e) => setRemark(e.target.value)} inputProps={{ maxLength: 500 }}
          sx={{ mb: 1, '& input': { fontSize: 12.5 }, '& label': { fontSize: 12.5 } }}
        />
        {error && (
          <Typography role='alert' sx={{ fontSize: 12, color: '#b91c1c', whiteSpace: 'pre-wrap', mb: 1 }}>{error}</Typography>
        )}

        <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mt: 2, mb: 0.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Recent uploads (last 7 days)</Typography>
          <Tooltip title='Refresh'><span><IconButton size='small' onClick={loadUploads} disabled={loadingUploads}><RefreshIcon fontSize='small' /></IconButton></span></Tooltip>
        </Stack>
        {uploads.length === 0 ? (
          <Typography sx={muted}>{loadingUploads ? 'Loading…' : 'No bulk uploads in the last 7 days.'}</Typography>
        ) : (
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>Uploaded</TableCell>
                <TableCell sx={headSx}>By · remark</TableCell>
                <TableCell sx={headSx} align='right'>AWBs</TableCell>
                <TableCell sx={headSx} align='right'>Done</TableCell>
                <TableCell sx={headSx} align='right'>Failed</TableCell>
                <TableCell sx={headSx}>Failures</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {uploads.map((u) => {
                const s = bulkFileSummary(u)
                return (
                  <TableRow key={u.uuid || u.id}>
                    <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                      {fmtDate(u.createdAt)}
                      <Typography sx={muted}>{s.inProgress ? 'Processing…' : 'Completed'}</Typography>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {u.createdBy || '—'}
                      <Typography sx={muted}>{u.remark || ''}</Typography>
                    </TableCell>
                    <TableCell sx={cellSx} align='right'>{s.total}</TableCell>
                    <TableCell sx={cellSx} align='right'>{s.ok}</TableCell>
                    <TableCell sx={{ ...cellSx, color: s.failed ? '#b91c1c' : undefined, fontWeight: s.failed ? 700 : 400 }} align='right'>{s.failed}</TableCell>
                    <TableCell sx={cellSx}>
                      {s.failures.length === 0 ? <Typography sx={muted}>—</Typography> : (
                        <Box sx={{ maxHeight: 120, overflowY: 'auto' }}>
                          {s.failures.map((f) => (
                            <Typography key={f.awb} sx={{ fontSize: 11.5 }}><b>{f.awb}</b>: {f.reason}</Typography>
                          ))}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={close} sx={{ textTransform: 'none', fontSize: 12.5 }}>Close</Button>
        <Button variant='contained' onClick={onUpload} disabled={uploading || !file} sx={{ textTransform: 'none', fontSize: 12.5 }}>
          {uploading ? 'Uploading…' : 'Upload and process'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
